import React from 'react';
import { EducationFormatter } from '@/utils/educationFormatter';

interface ResumeData {
  personalInfo: {
    name: string;
    title: string;
    email: string;
    phone: string;
    location: string;
    linkedin?: string;
    website?: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    dates: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    dates: string;
    gpa?: string;
  }>;
  skills: string[];
}

interface Template2Props {
  data: ResumeData;
}

export const Template2: React.FC<Template2Props> = ({ data }) => {
  return (
    <div className="max-w-4xl mx-auto bg-white text-gray-900 font-sans">
      <div className="grid grid-cols-3 gap-8">
        {/* Sidebar */}
        <div className="col-span-1 bg-gray-50 p-6 border-r border-gray-200">
          {/* Contact Info */}
          <div className="mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
              Contact
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>{data.personalInfo.email}</p>
              <p>{data.personalInfo.phone}</p>
              <p>{data.personalInfo.location}</p>
              {data.personalInfo.linkedin && <p>{data.personalInfo.linkedin}</p>}
              {data.personalInfo.website && <p>{data.personalInfo.website}</p>}
            </div>
          </div>

          {/* Skills */}
          <div className="mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
              Skills
            </h3>
            <div className="space-y-2">
              {data.skills.map((skill, index) => (
                <div key={index} className="text-sm text-gray-700">
                  {skill}
                </div>
              ))}
            </div>
          </div>

          {/* Education */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
              Education
            </h3>
            <EducationFormatter 
              education={data.education} 
              className="text-gray-700 text-sm"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="col-span-2 p-6">
          {/* Header */}
          <header className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{data.personalInfo.name}</h1>
            <h2 className="text-lg text-blue-600 font-medium mb-4">{data.personalInfo.title}</h2>
          </header>

          {/* Summary */}
          <section className="mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide border-b border-blue-600 pb-1">
              Professional Summary
            </h3>
            <p className="text-gray-700 leading-relaxed text-sm">{data.summary}</p>
          </section>

          {/* Experience */}
          <section>
            <h3 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide border-b border-blue-600 pb-1">
              Professional Experience
            </h3>
            
            {data.experience.map((job, index) => (
              <div key={index} className="mb-6">
                <div className="mb-2">
                  <h4 className="font-bold text-gray-900 text-sm">{job.title}</h4>
                  <div className="flex justify-between items-center">
                    <p className="text-blue-600 font-medium text-sm">{job.company}</p>
                    <p className="text-xs text-gray-600">{job.dates}</p>
                  </div>
                  <p className="text-xs text-gray-600">{job.location}</p>
                </div>
                
                <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm ml-4">
                  {job.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};